<template>
  <div class="well well-lg light-well library-panel">
    <div class="panel-header">
      <div class="folder-header">
        <div class="folder-icon">
          <i class="fa fa-folder-open text-success" aria-hidden="true"/>
        </div>
        <h3 :is='`h${headingLevel}`' class="folder-title">{{ currentContentFolderName }}</h3>
      </div>

      <div class="timestamp-section">
        <div class="saved-date">
          <i class="fa fa-calendar" aria-hidden="true"/>
          Saved {{ savedTimeText }}
        </div>
      </div>
    </div>

    <div class='folder-member-info' v-if="isLibraryFolderShared">
      <template v-if="libraryFolderOwner">
        <span class="owner-label">Owner:</span>
        <user-avatar
          :user-info="libraryFolderOwner"
        />
      </template>
      <template v-if="shouldShowContributor">
        <span class="contributor-label">Contributed by</span>
        <user-avatar
          :user-info="contributorInfo"
        />
      </template>
    </div>

    <LibrarySaveChangesButtons
      v-if="isCurrentUserContentOwner && !hideSaveButton"
      @save="$emit('save')"
      @discard="$emit('discard')"
      :removing="removing"
      :saving="contentSaveStatus === CONTENT_SAVE_STATES.SAVING"
      :unsaved-changes="contentSaveStatus === CONTENT_SAVE_STATES.UNSAVED"
      :just-saved="contentSaveStatus === CONTENT_SAVE_STATES.JUST_SAVED"
      :save-button-text="saveButtonText"/>

    <div class="access-notice" v-else-if="!hideSaveButton">
      <i class="fa fa-lock text-muted" aria-hidden="true"/>
      <span class="access-text">Only the Owner of this Shared Folder can make edits.</span>
    </div>

    <div class="action-buttons">
      <CopyGoalForAnotherStudentButton
        v-if="shouldShowCopyButton"
        :personalized="copyContentProps.personalized"
        :grade-level-goal="copyContentProps.gradeLevelGoal"
        :adapted-goals="copyContentProps.adaptedGoals"
        :source-goal-uuid="copyContentProps.goalUuid"
        :original-student="copyContentProps.originalStudent"
        :anchor-page="copyContentProps.anchorPage"/>
      <button
        v-if="shouldShowPrintButton"
        @click="$emit('print')"
        class="link-button">
        <i class="fa fa-print" aria-hidden="true"/> Print this {{ contentTypeLabel }}
      </button>
      <router-link
        v-if="originalPageLink"
        :to="originalPageLink">
        <i class="fa fa-share" />
        View original page
      </router-link>
      <button
        v-if="canRemoveCurrentContent"
        class="link-button text-danger"
        @click="handleRemoveClick"
        tabindex="0">
        <i class="fa fa-trash" aria-hidden="true"/>
        Remove from folder
      </button>
    </div>
  </div>
</template>

<script>
import UserAvatar from '@/components/UserAvatar.vue'
import CopyGoalForAnotherStudentButton from '@/components/goals/CopyGoalForAnotherStudentButton.vue'
import { mapActions, mapGetters, mapState } from 'vuex'
import LibrarySaveChangesButtons from '@/components/goals/LibrarySaveChangesButtons.vue'
import { CONTENT_SAVE_STATES } from '@/util/contentSaveStates'
import { CONTENT_TYPES } from '@/util/access_visibility'
import { toolkitUrls } from '@/links'

export default {
  name: 'LibraryContentPanel',
  components: { LibrarySaveChangesButtons, CopyGoalForAnotherStudentButton, UserAvatar },
  props: {
    contentType: {
      type: String,
      required: true
    },
    contentLastSave: {
      type: String,
      default: undefined
    },
    contentSaveStatus: {
      type: String,
      default: undefined
    },
    headingLevel: {
      type: Number,
      default: 3,
      validator: (value) => Number.isInteger(value) && value >= 1 && value <= 6
    },
    copyContentProps: {
      type: Object,
      default: () => null
    },
    sourceContentUuid: {
      type: String,
      default: undefined
    },
    hideSaveButton: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      showCopyModal: false,
      removing: false
    }
  },
  computed: {
    CONTENT_SAVE_STATES() {
      return CONTENT_SAVE_STATES
    },
    ...mapGetters('user', ['userId']),
    ...mapState('library', { contributorUUID: state => state.currentContentFolder.contentContributorUUID }),
    ...mapGetters('library', [
      'currentContentFolderName',
      'currentContentFolderUuid',
      'currentContentUuid',
      'libraryFolderOwner',
      'isLibraryFolderShared',
      'libraryFolderMembers',
      'canRemoveCurrentContent'
    ]),
    contentOwnerUuid() {
      if (this.libraryFolderOwner && this.libraryFolderOwner.uuid) {
        return this.libraryFolderOwner.uuid
      }
      return this.contributorUUID
    },
    isCurrentUserContentOwner() {
      return this.userId === this.contentOwnerUuid
    },
    contributorInfo() {
      return this.libraryFolderMembers.find(member => member.uuid === this.contributorUUID)
    },
    shouldShowContributor() {
      if (!this.contributorInfo || !this.libraryFolderOwner || !this.libraryFolderOwner.uuid) {
        return false
      }
      return this.contentOwnerUuid !== this.contributorUUID
    },
    savedTimeText() {
      return this.contentLastSave
    },
    saveButtonText() {
      if (this.removing) {
        return 'Removing...'
      }
      if (this.contentSaveStatus === CONTENT_SAVE_STATES.SAVING) {
        return 'Saving...'
      }
      if (this.contentSaveStatus === CONTENT_SAVE_STATES.UNSAVED) {
        return 'Save Changes'
      }
      if (this.contentSaveStatus === CONTENT_SAVE_STATES.JUST_SAVED) {
        return 'Saved'
      }
      return 'All Changes Saved'
    },
    shouldShowCopyButton() {
      const supportedContentCopyTypes = [CONTENT_TYPES.SAVED_SUPER_PAGE, CONTENT_TYPES.SAVED_GOAL]
      if (!supportedContentCopyTypes.includes(this.contentType)) {
        return false
      }
      if (!this.copyContentProps) {
        return false
      }
      const { personalized, goalUuid, anchorPage } = this.copyContentProps
      if (!goalUuid || personalized === undefined || anchorPage === undefined) {
        console.warn(`hiding LibraryContentPanel copy button due to invalid copyContentProps: ${this.copyContentProps}`)
        return false
      }
      return true
    },
    shouldShowPrintButton() {
      const supportedContentPrintTypes = [CONTENT_TYPES.SAVED_SUPER_PAGE, CONTENT_TYPES.SAVED_GOAL]
      return supportedContentPrintTypes.includes(this.contentType)
    },
    originalPageLink() {
      if (!this.sourceContentUuid) {
        return null
      }
      switch (this.contentType) {
        case CONTENT_TYPES.SAVED_SUPER_PAGE:
          return {name: 'AnchorPage', params: {uuid: this.sourceContentUuid}}
        case CONTENT_TYPES.SAVED_GOAL:
          return { name: 'GoalContentPage', params: { slug: this.sourceContentUuid, identifierAttribute: 'uuid' }}
        case CONTENT_TYPES.SAVED_STRATEGY:
          return {name: 'StrategyDetailPage', params: {slugOrUuid: this.sourceContentUuid}}
        default:
          return null
      }
    },
    contentTypeLabel() {
      switch (this.contentType) {
        case CONTENT_TYPES.SAVED_SUPER_PAGE:
        case CONTENT_TYPES.SAVED_GOAL:
          return 'goal'
        case CONTENT_TYPES.SAVED_STRATEGY:
          return 'strategy'
        case CONTENT_TYPES.PRESENTLEVEL:
          return 'present levels statement'
      }
    },
  },
  methods: {
    ...mapActions('library', ['deleteResource', 'fetchCurrentContentFolder']),
    toggleCopyModal() {
      this.currentModal = MODAL_TYPE_COPY
    },
    async handleRemoveClick(evt) {
      evt.preventDefault()
      let deleteFunction = null
      switch (this.contentType) {
        case CONTENT_TYPES.SAVED_SUPER_PAGE:
        case CONTENT_TYPES.SAVED_GOAL:
        case CONTENT_TYPES.SAVED_STRATEGY:
          deleteFunction = this.deleteSavedGoalOrStrategy
          break
        case CONTENT_TYPES.PRESENTLEVEL:
          deleteFunction = this.deleteSavedPresentlevels
          break
        default:
          throw new Error(`attempted to delete non-supported content type: ${this.contentType}`)
      }
      if (confirm(`Are you sure you want to delete this ${this.contentTypeLabel} from your library?`)) {
        this.removing = true
        try {
          await deleteFunction()
        } finally {
          this.removing = false
        this.$emit('presentlevel-removed')
        }
      }
    },
    async deleteSavedGoalOrStrategy() {
      this.$sendEvent('Clicked Move to Trash', {
        uuid: this.currentContentUuid,
        type: this.contentType
      })
      await this.deleteResource({
        uuid: this.currentContentUuid,
        collection: this.currentContentFolderUuid,
        type: this.contentType
      })
      window.location.href = toolkitUrls.libraryFolder(this.currentContentFolderUuid, true)
    },
    async deleteSavedPresentlevels() {
      this.$sendEvent('Clicked Remove Present Levels from Library')
      await this.deleteResource({
        uuid: this.currentContentUuid,
        type: this.contentType,
        collection: this.currentContentFolderUuid,
      })
      this.$emit('presentlevel-removed')
      await this.fetchCurrentContentFolder({
        contentUuid: this.currentContentUuid,
        userUuid: this.userId,
        contentType: CONTENT_TYPES.PRESENTLEVEL
      })
    }
  }
}
</script>

<style scoped lang="scss">
.library-panel {
  border: 1px solid #ddd;
  border-radius: 4px;
  background: #f0f5e3;
  padding: 20px;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 15px;

  .folder-header {
    display: flex;
    align-items: center;
    justify-content: center;

    .folder-title {
      color: var(--bg-toolkit-green-default);
      margin: 0;
    }
  }

  .folder-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 0.3rem;
    margin-top: 2px;

    .fa {
      font-size: 18px;
    }
  }

  .saved-date {
    font-size: 13px;
    color: var(--text-default, #333);

    .fa {
      margin-right: 4px;
    }
  }

  .folder-title {
    margin: 0 0 8px 0;
    font-size: 18px;
    font-weight: 600;
    color: #333;
  }
}

.folder-member-info {
  display: flex;
  align-items: center;
  margin: 0.5rem 0;
  font-size: 0.75rem;

  .owner-label,
  .contributor-label {
    color: #666;
  }

  .contributor-label {
    margin-left: 0.5rem;
  }
}

.access-notice {
  display: flex;
  align-items: center;
  padding: 0.3rem;
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  margin: 0.5rem 0;

  .fa {
    margin-right: 8px;
    color: #6c757d;
  }

  .access-text {
    font-size: 0.75rem;
    font-style: italic;
    color: var(--text-default, #333);
  }
}

.action-buttons {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-top: 0.5rem;

  :deep(.link-button) {
    padding: 0.1rem 0;
    margin: 0;
  }

  .link-button.text-danger {
    color: var(--red-default);
  }
}
</style>
